/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable react-hooks/exhaustive-deps */
import * as React from 'react'

import {
  Label,
  Button,
  PencilIcon,
  ChannelIcon,
  Flex,
  Tooltip,
} from '@buffer-mono/popcorn'

import { useAccount } from '~publish/contexts/AccountContext'
import { Breadcrumb } from '~publish/legacy/ai/components/AIAssistant/components/Breadcrumb'
import { getChannelColor, isService } from '~publish/helpers/channels'
import { Footer } from '~publish/legacy/ai/components/AIAssistant/components/Footer'
import { Onboarding } from '~publish/legacy/ai/components/AIAssistant/components/onboarding/Onboarding'
import { Prompt } from '~publish/legacy/ai/components/AIAssistant/components/Prompt'
import {
  selectContentGenerationStatus,
  resetContentGeneration,
  resetSelection,
  contentGenerationCancelled,
  selectContentGenerationSelection,
} from '~publish/legacy/ai/state/slice'
import { Services } from '~publish/legacy/constants'
import type { BufferEditor } from '~publish/legacy/editor/BufferEditor/types.plate'
import { useAppDispatch, useAppSelector } from '~publish/legacy/store'

import { MODULES, ACTIONS } from './AIAssistant.constants'
import { modulesMap } from './modules'

import styles from './AIAssistant.module.css'

import type { JSX } from 'react'

/**
 *
 */
const AIAssistantContext = React.createContext<{
  editor: BufferEditor | null
  channel: string | null
}>({
  editor: null,
  channel: null,
})

type ActiveModule = keyof typeof modulesMap | null

const getEditorContent = ({
  editor,
  editorContent,
}: {
  editor?: BufferEditor
  editorContent?: string
}): string | null => {
  return editorContent ?? editor?.getContent?.() ?? null
}

const getDefaultActiveModule = ({
  editor,
  editorContent,
}: {
  editor?: BufferEditor
  editorContent?: string
}): ActiveModule => {
  const content = getEditorContent({ editor, editorContent })
  return content ? null : MODULES.AIWrite.id
}

/**
 *
 */
function AIAssistant({
  editor,
  channel,
  initialPrompt,
  editorContent,
}: {
  editor?: BufferEditor
  channel?: string
  initialPrompt?: string | null
  editorContent?: string
}): JSX.Element {
  const [initAction, setInitAction] = React.useState<string | null>(null)
  const [initPrompt, setInitPrompt] = React.useState<string | null>(
    initialPrompt ?? null,
  )
  const [activeModule, setActiveModule] = React.useState<ActiveModule>(() =>
    getDefaultActiveModule({ editor, editorContent }),
  )
  const [expanded, setExpanded] = React.useState<boolean>(false)
  const dispatch = useAppDispatch()

  const { account } = useAccount()

  const {
    contentGenerationInProgress,
    contentGenerationResults,
    contentGenerationError,
  } = useAppSelector((state) => selectContentGenerationStatus(state))

  const contentGenerationSelection = useAppSelector((state) =>
    selectContentGenerationSelection(state),
  )

  const hasResults = contentGenerationResults.length > 0
  const hasContentGenerationStatus =
    hasResults || contentGenerationInProgress || !!contentGenerationError

  React.useLayoutEffect(() => {
    // Changes in editor content should update the visible AI mode before paint,
    // but should not replace result/loading/error screens.
    if (hasContentGenerationStatus) return
    resetActiveModule()
  }, [editor?.children, editorContent, hasContentGenerationStatus])

  React.useEffect(() => {
    resetActiveModule()
  }, [contentGenerationSelection])

  const resetActiveModule = (): void => {
    const newActiveModule = getDefaultActiveModule({ editor, editorContent })
    setActiveModule((currentActiveModule) =>
      newActiveModule !== currentActiveModule
        ? newActiveModule
        : currentActiveModule,
    )
  }

  const onBack = (): void => {
    const isRefineModule = activeModule === MODULES.AIRefine.id

    dispatch(contentGenerationCancelled())
    dispatch(resetContentGeneration())

    if (isRefineModule || (!hasResults && !contentGenerationInProgress)) {
      onReset()
    }
  }

  const onReset = (): void => {
    dispatch(contentGenerationCancelled())
    dispatch(resetContentGeneration())
    setInitAction(null)
    setInitPrompt(null)
    resetActiveModule()
  }

  const onClear = (): void => {
    editor?.removeSelection?.()
    dispatch(resetSelection())
    onReset()
  }

  const onWriteMore = (): void => {
    setInitAction('generate')
    setInitPrompt(null)
    setActiveModule(MODULES.AIWrite.id)
  }

  const onRefine = (action: string): void => {
    const content = getEditorContent({ editor, editorContent })
    const prompt = contentGenerationSelection || content

    setInitAction(action)
    setInitPrompt(prompt)
    setActiveModule(MODULES.AIRefine.id)
  }

  const onTogglePrompt = (): void => {
    setExpanded((isExpanded) => !isExpanded)
  }

  const serviceData = channel ? Services.get(channel) : null
  const serviceLabel = serviceData ? serviceData.formattedName : null

  const validChannel = isService(channel) ? channel : undefined
  const showChannel =
    validChannel !== undefined && serviceLabel && !contentGenerationSelection

  const channelIconStyle = {
    backgroundColor: validChannel ? getChannelColor(validChannel) : undefined,
  }

  const populatedEditor = !!getEditorContent({ editor, editorContent })

  const withBackButton: boolean =
    contentGenerationInProgress ||
    !!contentGenerationError ||
    contentGenerationResults.length > 0 ||
    (!!activeModule && populatedEditor)

  const ActiveModuleComponent = activeModule ? modulesMap[activeModule] : null

  const onboardingCompleted = account?.settings?.aiSettings?.onboarded
  const contextValue = React.useMemo(
    () => ({
      editor: editor ?? null,
      channel: channel && channel !== 'omni' ? channel : null,
    }),
    [editor, channel],
  )

  return (
    <div className={styles.wrapper} data-testid="ai-assistant-side-panel">
      <div className={styles.header}>
        <Breadcrumb onGoBack={withBackButton ? onBack : null} />
        {onboardingCompleted && showChannel && (
          <Tooltip
            sideOffset={8}
            side="bottom"
            content={`Tailoring for ${serviceLabel}`}
          >
            <Flex
              align="center"
              justify="center"
              className={styles.channelIcon}
              style={channelIconStyle}
            >
              <ChannelIcon type={validChannel} size="small" />
            </Flex>
          </Tooltip>
        )}
      </div>
      <div className={styles.contentWrapper}>
        {!onboardingCompleted ? (
          <Onboarding />
        ) : ActiveModuleComponent ? (
          <AIAssistantContext.Provider value={contextValue}>
            <ActiveModuleComponent
              initAction={initAction}
              initPrompt={initPrompt}
              onClear={onClear}
            />
          </AIAssistantContext.Provider>
        ) : (
          <div className={styles.idleWrapper}>
            {contentGenerationSelection ? (
              <div className={styles.selectionWrapper}>
                <Label>Using this text:</Label>
                <Prompt
                  prompt={contentGenerationSelection}
                  expanded={expanded}
                  onToggle={onTogglePrompt}
                  onClear={onClear}
                  lines={2}
                  withBackground
                />
              </div>
            ) : (
              <div className={styles.writeMoreWrapper}>
                <Label>How can I help with this post?</Label>
                <Button
                  onClick={onWriteMore}
                  variant="secondary"
                  data-test-id="ai-assistant-write-more-button"
                  className={styles.purpleButton}
                >
                  <PencilIcon /> Write More
                </Button>
              </div>
            )}
            <div
              className={styles.actionsWrapper}
              id="ai-assistant-fine-tuning-container"
            >
              {['rephrase', 'shorten', 'expand'].map((action) => {
                const actionConfig = Object.entries(ACTIONS).find(
                  ([actionKey]) => actionKey === action,
                )?.[1]
                if (!actionConfig?.IconComponent) return null
                const { IconComponent: ActionIcon } = actionConfig

                return (
                  <Button
                    key={action}
                    onClick={(): void => onRefine(action)}
                    variant="secondary"
                    data-test-id={`ai-assistant-fine-tuning-${action}-button`}
                    className={styles.purpleButton}
                  >
                    <ActionIcon />
                    {`
                      ${actionConfig.label}
                      ${showChannel ? ` for ${serviceLabel}` : ''}
                    `}
                  </Button>
                )
              })}
            </div>
          </div>
        )}
      </div>
      {onboardingCompleted && <Footer />}
    </div>
  )
}

export { AIAssistant, AIAssistantContext }
